// DownloadMenu — top-right dropdown on KB detail pages.
//
// Two actions:
//   • PDF — html-to-image screenshot of pageRef, embedded into a jsPDF
//     document that matches the element's exact dimensions.
//   • Markdown — clean per-block serializer over the BlockRenderer recipe.
//
// Ported from my-jarvis-dashboard-erez (MJOS-101). Wired into BlockKbPage
// so every /kb-doc/* page on dnlcohen87 gets it.

import { useState, type RefObject } from "react";
import { Download, FileText, FileType2 } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { downloadAsPdf } from "@/lib/download-pdf";
import { downloadAsMarkdown } from "@/lib/download-md";
import type { Block } from "@/components/blocks/BlockRenderer";

interface DownloadMenuProps {
  pageRef: RefObject<HTMLElement | null>;
  blocks: Block[];
  /** Used for the saved filename (without extension). */
  filename: string;
}

export function DownloadMenu({ pageRef, blocks, filename }: DownloadMenuProps) {
  const [busy, setBusy] = useState<"pdf" | null>(null);

  async function handlePdf() {
    if (!pageRef.current || busy) return;
    setBusy("pdf");
    try {
      await downloadAsPdf(pageRef.current, `${filename}.pdf`);
    } catch (err) {
      console.error("PDF export failed:", err);
    } finally {
      setBusy(null);
    }
  }

  function handleMarkdown() {
    try {
      downloadAsMarkdown(blocks, filename);
    } catch (err) {
      console.error("Markdown export failed:", err);
    }
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" disabled={busy !== null}>
          <Download className="mr-1.5 h-3.5 w-3.5" />
          {busy === "pdf" ? "Building PDF…" : "Download"}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onSelect={handlePdf} disabled={busy !== null}>
          <FileType2 className="mr-2 h-4 w-4" />
          PDF
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={handleMarkdown}>
          <FileText className="mr-2 h-4 w-4" />
          Markdown
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
